import React, { useState, useEffect, useMemo } from 'react';
import { CnBox, CnAsyncSelect, CnInput, CnDatePickerPro, CnRangeDatePickerPro, CnPercentInput } from '@sto/cn-ui';
import { CustomRemoteSelect } from '@/components';
import dayjs from 'dayjs';
import styles from './index.module.scss';

interface IFormFieldGroupProps {
  value?: any;
  onChange?: (value: any) => void;
  layout?: 'horizontal' | 'vertical';
  properties: any;
  disabled?: boolean;
  readOnly?: boolean;
}

/**
 * 范围日期预设
 */
const RangePreset = {
  今天: [dayjs(), dayjs()],
  本月: [dayjs().startOf('month'), dayjs().endOf('month')],
}; 

/**
 * 组合表单项（组合各种输入和展示组件）
 * @returns 
 */
const FormFieldGroup = ({
  value = {},
  onChange,
  layout = 'horizontal',
  properties,
  disabled = false,
  readOnly = false,
}: IFormFieldGroupProps) => {
  const [values, setValues] = useState<any>({});

  useEffect(() => {
    if (value) {
      setValues(value);
    }
  }, [value]);

  const handleChange = (key: string, val: any) => {
    onChange?.({ ...values, [key]: val });
  };

  const renderFields = useMemo(() => {    
    if (properties) {
      const keys = Object.keys(properties);
      return keys.map(key => {
        const property = properties[key];
        const component = property['x-component'];
        const condition = property['x-condition'] || property['x_condition']; // 条件
        if (condition) {
          const dependsOn = condition['dependsOn']; // 依赖的属性
          const when = condition['when']; // 条件
          if (dependsOn && when) {
            // 将条件表达式中的 dependValue 替换为实际的值
            const conditionExpression = when.replace(/dependValue/g, `values['${dependsOn}']`);
            
            // 使用 Function 构造函数安全地执行条件表达式
            try {
              const conditionFunction = new Function('values', `return ${conditionExpression}`);
              const shouldShow = conditionFunction(values);
              
              if (!shouldShow) {
                // 不展示的项，值置空
                if (values[key]) {
                  onChange?.({ ...values, [key]: null });
                }
                return null;
              }
            } catch (error) {
              console.warn('条件表达式执行失败:', error, '表达式:', conditionExpression);
              // 如果条件表达式执行失败，默认显示该字段
            }
          }
        }
        if (component) {
          switch (component) {
            case 'CnSelect':
              // 普通下拉选择
              return <CnAsyncSelect 
                value={values[key]} 
                onChange={(val) => handleChange(key, val)} 
                {...property['x-component-props']} 
                key={key} 
                disabled={disabled}
                readOnly={readOnly}
              />;
            case 'CustomRemoteSelect':
              // 远程加载下拉选择
              return <CustomRemoteSelect 
                value={values[key]} 
                onChange={(val) => handleChange(key, val)}
                style={{ maxWidth: '500px' }} 
                {...property['x-component-props']} 
                key={key} 
                disabled={disabled}
                readOnly={readOnly}
              />;
            case 'CnInput':
              return <CnInput 
                value={values[key]} 
                onChange={(val) => handleChange(key, val)} 
                key={key} 
                style={{ width: '200px' }} 
                {...property['x-component-props']} 
                disabled={disabled}
                readOnly={readOnly}
              />;
            case 'CnDatePicker':
              // 单选日期
              return <CnDatePickerPro 
                value={values[key]} 
                onChange={(val) => handleChange(key, val)} 
                key={key} 
                {...property['x-component-props']} 
                disabled={disabled}
                readOnly={readOnly}
              />;
            case 'CnRangeDatePicker': {
              const componentProps = property['x-component-props'];
              const { showPreset, ...rest } = componentProps;
              // 范围日期
              return <CnRangeDatePickerPro 
                value={values[key]} 
                onChange={(val) => handleChange(key, val)} 
                key={key} 
                preset={showPreset ? RangePreset : undefined}
                {...rest} 
                disabled={disabled}
                readOnly={readOnly}
              />;
            }
            case 'CnPercentInput': {
              return <CnPercentInput 
                {...property['x-component-props']} 
                value={values[key]} 
                onChange={(val) => handleChange(key, val)} 
                key={key} 
                disabled={disabled}
                readOnly={readOnly}
              />;
            }
            default:
              return null;
          }
        }
      });
    }
    return null;
  }, [properties, values]);

  return (
    <CnBox
      direction="row"
      align="center"
      wrap="wrap"
      className={styles.compositeFieldBox}
    >
      {renderFields}
    </CnBox>
  );
};

export default FormFieldGroup;